import { searchRequestStore } from '@/stores/searchRequestStore';
import axios from 'axios';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { AiPlanInfo, AiPlansResponse, RecommendedPlansRequest, VehicleAnalysis } from '~/dtos/ai-analysis';
import { SearchRequest } from '~/dtos/plan-search';
import { API_URL } from '~/modules/config';
import { selectData } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { globalStores } from '@/util/globalStores';

export interface AiAnalysisStoreState {
  isLoadingAnalysis: boolean;
  isLoadingRecommendedPlans: boolean;
  showAnalysis: boolean;
  vehicleAnalysis: VehicleAnalysis;
  searchRequest: SearchRequest;
  recommendedPlans: AiPlanInfo[];
}

const initialState: AiAnalysisStoreState = {
  isLoadingAnalysis: false,
  isLoadingRecommendedPlans: false,
  showAnalysis: false,
  vehicleAnalysis: {
    vehicle: {
      year: 0,
      make: '',
      model: '',
    },
    commonIssues: [],
  },
  searchRequest: {
    searchType: '',
    state: '',
    mileage: '',
    invalidSearch: null,
  },
  recommendedPlans: [],
};

const LOCAL_STORAGE_KEY = 'aiAnalysisStoreState';
let localStorageState;

export const aiAnalysisStore = createStore(
  initialState,
  (set, get) => ({
    changeIsLoading(changes: Partial<AiAnalysisStoreState['isLoadingAnalysis']>) {
      set(function changeIsLoading(draft) {});
    },
    changeShowAnalysis(changes: Partial<AiAnalysisStoreState['showAnalysis']>) {
      set(function changeShowAnalysis(draft) {
        draft.showAnalysis = changes;
      });
    },
    async fetchAnalysis() {
      try {
        set(function setLoading(draft) {
          draft.isLoadingAnalysis = true;
          draft.isLoadingRecommendedPlans = false;
        });

        const searchRequest = searchRequestStore.getState().searchRequest;
        const analysisResponse = await axios
          .post(`${API_URL}/ai/analysis`, searchRequest)
          .then(selectData<VehicleAnalysis>);

        set(function setVehicleAnalysis(draft) {
          draft.isLoadingAnalysis = false;
          draft.isLoadingRecommendedPlans = true;
          draft.vehicleAnalysis = analysisResponse;
        });

        const recommendedPlansRequest: RecommendedPlansRequest = {
          searchRequest: searchRequest,
          vehicleAnalysis: analysisResponse,
        };
        const recommendedPlansResponse = await axios
          .post(`${API_URL}/ai/plans`, JSON.stringify(recommendedPlansRequest), {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then(selectData<AiPlansResponse>);

        set(function setRecommendedPlans(draft) {
          draft.isLoadingRecommendedPlans = false;
          draft.searchRequest = recommendedPlansResponse.searchRequest;
          draft.recommendedPlans = recommendedPlansResponse.selectedPlans;
        });
      } catch (error) {
        console.error('Error fetching analysis:', error);
        set(function failedLoading(draft) {
          draft.isLoadingAnalysis = false;
        });
      }
    },
  }),
  {
    storeName: 'aiAnalysisStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

try {
  if (typeof localStorage !== 'undefined') {
    localStorageState = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? 'null');
    if (localStorageState) {
      localStorageState.isLoading = true;
      aiAnalysisStore.setState(localStorageState);
    }
  }
} catch (error) {
  localStorageState = initialState;
  handleException(error);
}

// Save state to local storage
aiAnalysisStore.subscribe((state) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }
});

// This hook is used to connect react components to the store
export const useAiAnalysisStore = createUseStore(aiAnalysisStore);

export function selectAiAnalysisIsLoadingAnalysis(state: AiAnalysisStoreState = aiAnalysisStore.getState()) {
  return state.isLoadingAnalysis;
}

export function selectAiAnalysisIsLoadingRecommendedPlans(state: AiAnalysisStoreState = aiAnalysisStore.getState()) {
  return state.isLoadingRecommendedPlans;
}

export function selectAiAnalysisShowAnalysis(state: AiAnalysisStoreState = aiAnalysisStore.getState()) {
  return state.showAnalysis;
}

export function selectAiVehicleAnalysis(state: AiAnalysisStoreState = aiAnalysisStore.getState()) {
  return state.vehicleAnalysis;
}

export function selectAiRecommendedPlans(state: AiAnalysisStoreState = aiAnalysisStore.getState()) {
  return state.recommendedPlans;
}
